<script setup lang="ts">
// components
import type { LocationQuery } from 'vue-router';
import CiModalHeader from '@/components/CiModal/CiModalHeader.vue';

const CiMap = defineAsyncComponent({
  loader: () => import('@/components/map/CiMap.vue'),
});

/********************
 * PROPS & EMITS     *
 ********************/
export interface CiMapModalProps {
  campsite: any;
  closeable?: boolean;
  latitude: string;
  longitude: string;
  query?: any;
  title?: string | null;
}
const props = withDefaults(defineProps<CiMapModalProps>(), {
  title: null,
  closeable: true,
  query: () => {},
});

const emit = defineEmits<{
  close: [];
}>();

/********************
 * COMPOSITIONS      *
 ********************/
const localePath = useLocalePath();
const route = useRoute();
const searchStore = useSearchStore();
const { $gettext } = useGettext();
const { locale } = useI18n();

/********************
 * REFS & VARS       *
 ********************/
const initialArea = ref<string | null>(null);
const mapRef = ref<InstanceType<typeof CiMap> | null>(null);

/********************
 * FUNCTIONS         *
 ********************/
async function getCampsites(query: LocationQuery) {
  // Use higher limit param in maps view and disable offset
  delete query.offset;
  query.campsite = props.campsite.slug;
  query.force_cluster = 'true';
  query.limit = '200';
  query.zl = Math.round(mapRef.value?.getMapZoomLevel() || 4).toString();

  // query are the query params taken from the url, passed as props via the router
  const response = await searchStore.getMapSearchResults({ lang: locale.value, query });
  if (mapRef.value) {
    mapRef.value.setMarkers(response.results, false);
  }
}

function onMapMounted(payload: any) {
  const area = Object.values(payload.bbox).join(',');

  if (!initialArea.value) {
    initialArea.value = area;
  }

  const query = {
    ...props.query,
    area,
    zl: 13,
  };

  getCampsites(query);
}

function goToMap() {
  const query = { ...props.query, area: initialArea.value };
  delete query.country;
  delete query.federal_state;
  delete query.region;
  query.campsite = props.campsite.slug;
  query.zl = 13;

  emit('close');
  return navigateTo(localePath({ name: RBN_SEARCH_MAP, query }));
}
</script>

<template>
  <div
    v-if="props.campsite && props.longitude && props.latitude"
    class="ci-map__modal flex h-full flex-col rounded-lg bg-white shadow-2xl"
  >
    <CiModalHeader
      :title="props.title"
      :closeable="props.closeable"
      @modal-close="props.closeable ? emit('close') : false"
    />

    <div class="relative m-4 grow overflow-hidden">
      <CiMap
        ref="mapRef"
        :lat="props.latitude"
        :lng="props.longitude"
        :query="route.query"
        :zoom="13"
        :pre-selected="props.campsite"
        @map-change-bounds="onMapMounted"
      />
    </div>

    <div class="mb-4 flex justify-center">
      <CiButton
        class="button button--primary"
        @click="goToMap"
      >
        <span>{{ $gettext('Open full screen map') }}</span>
      </CiButton>
    </div>
  </div>
</template>
